<script setup>
  import { ref, onMounted, computed, reactive } from 'vue'
  import { useRouter, useRoute } from 'vue-router'
  import Header from '@/components/Header.vue'
  import Footer from '@/components/Footer.vue'
  import { useDialogStore } from '@/stores/dialogStore'
  import { useTodoMasterStore } from '@/stores/todoMasterStore'
  import TodoEditor from '@/components/TodoEditor.vue'

  const router = useRouter()
  const route = useRoute()

  const dialogStore = useDialogStore()
  const todoMasterStore = useTodoMasterStore()

  const breadcrumbs = ref([])
  const todoEditorRef = ref(null)
  const editorInitialData = ref([])

  // data ///////////////////////////////////////////

  // computed ///////////////////////////////////////

  const isNew = computed(() => {
    return route.params.todoMasterId === 'new'
  })

  const pageTitle = computed(() => {
    return isNew.value ? '案件マスタ新規作成' : '案件マスタ詳細'
  })

  const todoName = computed({
    get: () => (todoMasterStore.formData ? todoMasterStore.formData.name : ''),
    set: value => {
      if (todoMasterStore.formData) {
        todoMasterStore.formData.name = value
      }
    },
  })

  // methods ////////////////////////////////////////
  onMounted(async () => {
    // パンくずリストを設定
    setupBreadcrumbs()

    // 空のデータをセット
    todoMasterStore.clearDetailData()

    if (!isNew.value) {
      if (!(await todoMasterStore.getTodoMasterDetail(route.params.todoMasterId))) {
        router.push('/todo/todomaster')
        return
      }
      editorInitialData.value = todoMasterStore.getTodoJson
    }
  })

  // パンくずリストを設定する関数
  const setupBreadcrumbs = () => {
    // 基本のパンくずリスト項目
    const baseBreadcrumbs = [
      { name: 'ダッシュボード', url: '/dashboard' },
      { name: '案件進捗一覧', url: '/todo' },
      { name: '案件マスタ一覧', url: '/todo/todomaster' },
      { name: isNew.value ? '案件マスタ新規作成' : '案件マスタ詳細', url: '' },
    ]

    // breadcrumbsを更新
    breadcrumbs.value = baseBreadcrumbs
  }

  // 戻るボタン
  const goBack = () => {
    // 戻る前にクリアする
    todoMasterStore.clearDetailData()
    router.back()
  }

  // TodoEditorからのデータ保存
  const handleSave = async data => {
    // ストア経由で保存
    const response = await todoMasterStore.saveTodoMaster(data)

    if (response) {
      // 保存成功時に現在の状態をバックアップとして保存
      if (todoEditorRef.value) {
        todoEditorRef.value.updateBackup()
      }

      if (await dialogStore.showMessageDialog(isNew.value ? '新規作成' : '保存', response.message)) {
        // 新規作成の場合、編集モードに移行する
        if (isNew.value && todoMasterStore.formData.id) {
          // URLを更新して編集モードに変更
          router.replace(`/todo/todomaster/${response.todoMasterId}`)
        }
      }
    }
  }

  // 新規登録・保存ボタン
  const saveTodoMaster = async () => {
    // TodoEditorの保存をトリガー
    if (todoEditorRef.value) {
      todoEditorRef.value.triggerSave()
    }
  }

  // 削除ボタン
  const deleteTodoMaster = async () => {
    // 確認ダイアログ
    if (await dialogStore.showConfirmDialog('削除', '本当に削除しますか？')) {
      const result = await todoMasterStore.deleteTodoMaster(todoMasterStore.formData.id)

      if (result) {
        if (await dialogStore.showMessageDialog('削除', '削除が完了しました')) {
          // 削除成功時は一覧画面に戻る
          router.push('/todo/todomaster')
        }
      }
    }
  }
</script>

<template>
  <Header />
  <main class="app-main">
    <div class="main-header">
      <button type="button" class="back" @click="goBack">
        <span class="material-symbols-outlined">arrow_back</span>
      </button>
      <h2>{{ pageTitle }}</h2>
      <button v-if="!isNew" type="button" class="delete-btn" @click="deleteTodoMaster">削除</button>
      <button type="button" class="save-btn" @click="saveTodoMaster">
        {{ '保存' }}
      </button>
    </div>

    <div class="main-body">
      <!-- 編集フォーム -->
      <div class="form-container">
        <!-- 基本情報フォーム -->
        <div class="form-section">
          <div class="form-group">
            <label for="todoName">
              種別名
              <span class="required">*</span>
            </label>
            <input type="text" id="todoName" v-model="todoName" placeholder="種別名を入力してください" class="form-control" />
          </div>
        </div>

        <!-- TodoEditor -->
        <div class="editor-section">
          <h3>フロー編集</h3>
          <div class="editor-container">
            <TodoEditor ref="todoEditorRef" :initial-data="editorInitialData" @save="handleSave" />
          </div>
        </div>
      </div>
    </div>
  </main>
  <Footer :breadcrumbs="breadcrumbs" />
</template>

<style lang="less" scoped>
  .material-symbols-outlined {
    vertical-align: middle;
  }

  .form-container {
    background-color: #fff;
    padding: 20px;
    display: grid;
    grid-template-rows: auto 1fr;
    padding: 20px;
  }

  .form-section {
  }

  .form-group {
    margin-bottom: 15px;

    label {
      display: block;
      margin-bottom: 5px;
      font-weight: 500;

      .required {
        color: #f44336;
      }
    }

    .form-control {
      width: 100%;
      padding: 8px 12px;
      border: 1px solid #ddd;
      border-radius: 4px;
      font-size: 14px;

      &:focus {
        border-color: #2196f3;
        outline: none;
      }

      &:disabled {
        background-color: #f5f5f5;
        cursor: not-allowed;
      }
    }
  }

  .editor-section {
    h3 {
      margin-bottom: 10px;
      font-size: 16px;
      font-weight: 500;
    }
    display: grid;
    grid-template-rows: auto 1fr;
  }

  .editor-container {
    border: 1px solid #eee;
    border-radius: 4px;
    overflow: hidden;
  }

  .save-btn {
    background-color: #1a7247;
    color: #f7f7f7;
    border: none;
    border-radius: 15px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 20px;
    height: 30px;
    font-size: 12px;
    font-weight: bold;
    transition: opacity 0.3s;
    white-space: nowrap;

    &:hover {
      opacity: 0.7;
    }
  }

  .delete-btn {
    background-color: var(--alert-color);
    color: #fff;
    border: none;
    border-radius: 15px;
    font-size: 12px;
    font-weight: bold;
    width: 68px;
    min-width: 68px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: opacity 0.3s;
    white-space: nowrap;

    &:hover {
      opacity: 0.7;
    }
  }

  .main-header {
    display: flex;
    align-items: center;

    h2 {
      flex-grow: 1;
      margin: 0;
    }
  }
</style>
